import React from 'react';
import {Alert, Button, Input, Modal, Typography, Upload} from 'antd';
import {InboxOutlined} from '@ant-design/icons';
import {useAtom} from 'jotai';
import {RcFile} from 'antd/lib/upload';
import {confirmationModalAtom} from './types';

const {Dragger} = Upload;

const {Text} = Typography;

interface BatchCreationModalProps {
    modalVisible: boolean;
    batchName: string;
    setBatchName: (name: string) => void;
    batchDescription: string;
    setBatchDescription: (description: string) => void;
    handleModalSubmit: () => Promise<void>;
    onCancel: () => void;
}

interface ReuploadExcelModalProps {
    reuploadModalVisible: boolean;
    setReuploadModalVisible: (visible: boolean) => void;
    excelFile: RcFile | null;
    setExcelFile: (file: RcFile | null) => void;
    uploadingExcel: boolean;
    handleExcelUpload: (isReupload: boolean) => Promise<void>;
    uploadError: string | null;
    setUploadError: (error: string | null) => void;
}

export const BatchCreationModal: React.FC<BatchCreationModalProps> = ({
                                                                          modalVisible,
                                                                          batchName,
                                                                          setBatchName,
                                                                          batchDescription,
                                                                          setBatchDescription,
                                                                          handleModalSubmit,
                                                                          onCancel
                                                                      }) => {
    return (
        <Modal
            title="Create New Batch"
            open={modalVisible}
            onOk={handleModalSubmit}
            onCancel={onCancel}
            okText="Create Batch"
            cancelText="Cancel"
            closable={false}
            maskClosable={false}
            keyboard={false}
        >
            <div style={{marginBottom: 16}}>
                <Text>Please enter a name for your new batch:</Text>
                <Input
                    placeholder="Batch Name"
                    value={batchName}
                    onChange={(e) => setBatchName(e.target.value)}
                    style={{marginTop: 8}}
                    autoFocus
                />
            </div>

            <div>
                <Text>Description (optional):</Text>
                <Input.TextArea
                    placeholder="Batch Description"
                    value={batchDescription}
                    onChange={(e) => setBatchDescription(e.target.value)}
                    style={{marginTop: 8}}
                    rows={4}
                />
            </div>
        </Modal>
    );
};

export const ReuploadExcelModal: React.FC<ReuploadExcelModalProps> = ({
                                                                          reuploadModalVisible,
                                                                          setReuploadModalVisible,
                                                                          excelFile,
                                                                          setExcelFile,
                                                                          uploadingExcel,
                                                                          handleExcelUpload,
                                                                          uploadError,
                                                                          setUploadError
                                                                      }) => {
    return (
        <Modal
            title="Re-upload Excel File"
            open={reuploadModalVisible}
            onCancel={() => {
                setReuploadModalVisible(false);
                setUploadError(null); // Clear error when closing modal
            }}
            footer={null}
            width={600}
        >
            <div style={{marginBottom: 16}}>
                <Text>
                    Re-uploading an Excel file will delete all existing releases and tracks that were created for this
                    batch and replace them with the new parsed entries.
                </Text>
            </div>

            {uploadError && (
                <Alert
                    message="Upload Error"
                    description={uploadError}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setUploadError(null)}
                    style={{marginBottom: 16}}
                />
            )}

            <div style={{marginTop: 24}}>
                <Dragger
                    name="file"
                    multiple={false}
                    beforeUpload={(file) => {
                        setExcelFile(file);
                        // Clear any previous errors when a new file is selected
                        setUploadError(null);
                        return false;
                    }}
                    onRemove={() => {
                        setExcelFile(null);
                        // Clear any errors when the file is removed
                        setUploadError(null);
                    }}
                    fileList={(excelFile ? [excelFile] : []) as any}
                >
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined/>
                    </p>
                    <p className="ant-upload-text">Click or drag Excel file to this area to upload</p>
                    <p className="ant-upload-hint">
                        Support for a single Excel file only. The file should follow the template format.
                    </p>
                </Dragger>
            </div>

            <div style={{marginTop: 24, textAlign: 'right'}}>
                <Button
                    onClick={() => setReuploadModalVisible(false)}
                    style={{marginRight: 8}}
                >
                    Cancel
                </Button>
                <Button
                    type="primary"
                    onClick={() => handleExcelUpload(true)}
                    loading={uploadingExcel}
                    disabled={!excelFile}
                >
                    Re-upload and Process
                </Button>
            </div>
        </Modal>
    );
};

export const ConfirmationModal: React.FC<{
    handleConfirmedSubmit: () => Promise<boolean>;
}> = ({handleConfirmedSubmit}) => {
    const [confirmationModal, setConfirmationModal] = useAtom(confirmationModalAtom);

    return (
        <Modal
            title="Confirm Submission"
            open={confirmationModal.visible}
            onOk={handleConfirmedSubmit}
            onCancel={() => setConfirmationModal({...confirmationModal, visible: false})}
            okText="Submit"
            cancelText="Cancel"
            width={600}
        >
            <div style={{marginBottom: 16}}>
                <h3>The following will be created upon submission:</h3>
                <ul>
                    {confirmationModal.upcCount > 0 && (
                        <li>
                            <strong>{confirmationModal.upcCount}</strong> UPC(s) will be created for releases
                        </li>
                    )}
                    {confirmationModal.isrcCount > 0 && (
                        <li>
                            <strong>{confirmationModal.isrcCount}</strong> ISRC(s) will be created for tracks
                        </li>
                    )}
                </ul>
            </div>

            {confirmationModal.validationWarnings.length > 0 && (
                <div style={{marginBottom: 16}}>
                    <h3>Warning: There are still validation issues:</h3>
                    <ul>
                        {confirmationModal.validationWarnings.map((warning, index) => (
                            <li key={index}>
                                {warning.group === 'track' ? 'Track' : 'Release'}: {warning.message} ({warning.field})
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div style={{marginTop: 16, padding: 16, backgroundColor: '#f0f2f5', borderRadius: 4}}>
                <p><strong>Note:</strong> After submission, you can upload these releases to DSPs via the Releases
                    page.</p>
            </div>
        </Modal>
    );
};
